import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import {
  selectBookkeepingDocuments,
  selectIncorporationDocuments,
  selectOtherDocuments,
  selectReceiptDocuments,
  selectTaxDocuments,
  selectUserBankDocuments,
} from './lib/userDocuments.selector'

import {
  sortDocumentsByFileNameAsc,
  sortDocumentsByNameAscWithinCreatedAtDesc,
  sortDocumentsByNameAscWithinStatementDateDesc,
} from './lib/helpers'

import {
  BankStatements,
  Bookkeeping,
  Tax,
  Incorporation,
  Receipts,
  Other,
} from './tabs'

import { useAnalyticsView } from '@/features/amplitude'
import { PageHeader } from '@/components/PageHeader'
import { FeatureStateProvider } from './lib/state'
import { fetchUserDocuments } from './lib/userDocuments.slice'

import { GroupByEnum } from './table/helpers'
import { Tabs, Select } from '@/ui'
import { ConfirmDeleteDocumentDialog } from './shared/ConfirmDeleteDocumentDialog'
import { fetchUserDocumentCategoriesIfNeeded } from '@/features/admin/user-document-categories/userDocumentCategories.slice'

export const Documents = () => {
  const bankStatementDocuments = useReselector(selectUserBankDocuments)
  const bookkeepingDocuments = useReselector(selectBookkeepingDocuments)
  const taxRelatedDocuments = useReselector(selectTaxDocuments)
  const incorporationDocuments = useReselector(selectIncorporationDocuments)
  const receiptDocuments = useReselector(selectReceiptDocuments)
  const otherDocuments = useReselector(selectOtherDocuments)
  const [searchParams, setSearchParams] = useSearchParams()
  const defaultTab = searchParams.get('tab')
  const dispatch = useAppDispatch()
  const pageView = useAnalyticsView()
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  const [activeTab, setActiveTab] = useState(() => {
    switch (defaultTab) {
      case 'bookkeeping':
        return 'bookkeeping'
      case 'taxes':
      case 'tax':
        return 'taxes'
      case 'incorporation':
        return 'incorporation'
      case 'receipts':
        return 'receipts'
      case 'other':
        return 'other'
      case 'bank-statements':
      default:
        return 'bank-statements'
    }
  })

  const updateTab = (value: string) => {
    setSearchParams({ tab: value }, { replace: true })
    setActiveTab(value)
  }

  useEffect(() => {
    pageView('documents')
  }, [pageView])

  useEffect(() => {
    dispatch(fetchUserDocuments())
    dispatch(fetchUserDocumentCategoriesIfNeeded())
  }, [dispatch])

  const tabPanes = [
    {
      value: 'bank-statements',
      label: 'Bank Statements',
      content: (
        <BankStatements
          documents={bankStatementDocuments}
          tableOptions={{
            sortFn: sortDocumentsByNameAscWithinStatementDateDesc,
            groupBy: GroupByEnum.StatementYear,
          }}
        />
      ),
    },
    {
      value: 'bookkeeping',
      label: 'Bookkeeping',
      content: (
        <Bookkeeping
          documents={bookkeepingDocuments}
          tableOptions={{
            sortFn: sortDocumentsByNameAscWithinCreatedAtDesc,
            groupBy: GroupByEnum.CreatedAtYear,
          }}
        />
      ),
    },
    {
      value: 'taxes',
      label: 'Taxes',
      content: (
        <Tax
          documents={taxRelatedDocuments}
          tableOptions={{
            sortFn: sortDocumentsByNameAscWithinCreatedAtDesc,
            groupBy: GroupByEnum.CategoryYear,
          }}
        />
      ),
    },
    {
      value: 'incorporation',
      label: 'Incorporation',
      content: (
        <Incorporation
          documents={incorporationDocuments}
          tableOptions={{
            sortFn: sortDocumentsByFileNameAsc,
            groupBy: GroupByEnum.None,
          }}
        />
      ),
    },
    {
      value: 'receipts',
      label: 'Receipts',
      content: (
        <Receipts
          documents={receiptDocuments}
          tableOptions={{
            sortFn: sortDocumentsByNameAscWithinCreatedAtDesc,
            groupBy: GroupByEnum.MonthWithinYear,
          }}
        />
      ),
    },
    {
      value: 'other',
      label: 'Other',
      content: (
        <Other
          documents={otherDocuments}
          tableOptions={{
            sortFn: sortDocumentsByNameAscWithinCreatedAtDesc,
            groupBy: GroupByEnum.None,
          }}
        />
      ),
    },
  ]

  return (
    <div>
      <FeatureStateProvider>
        <PageHeader header="Your Documents" />

        <div className="mt-10">
          {isMobile ? (
            <div className="flex flex-col gap-6">
              <Select
                value={activeTab}
                options={tabPanes.map(({ value, label }) => ({
                  value,
                  text: label,
                }))}
                onValueChange={updateTab}
              />
              {tabPanes.find((pane) => pane.value === activeTab)?.content}
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={updateTab}>
              <Tabs.List>
                {tabPanes.map(({ value, label }) => (
                  <Tabs.Trigger key={value} value={value}>
                    {label}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>

              {tabPanes.map(({ value, content }) => (
                <Tabs.Content key={value} value={value}>
                  <div className="mt-6">{content}</div>
                </Tabs.Content>
              ))}
            </Tabs>
          )}
        </div>
        <ConfirmDeleteDocumentDialog />
      </FeatureStateProvider>
    </div>
  )
}
